/**
 * Origin allowlist for partner-aware API base URLs.
 *
 * The partner-resolution flow stores a partner-specific `apiBaseUrl` in
 * sessionStorage (`PARTNER_CONFIG`). The data provider attaches the MSAL Bearer
 * token to every request to that URL, so a poisoned `apiBaseUrl` (e.g. via an XSS
 * vector) could redirect authenticated calls to an attacker host and exfiltrate
 * the token. This module is the single source of truth for which origins are
 * allowed to receive those authenticated requests (see ADO #5097, and #5137 AC7
 * which reuses this list for the CSP `connect-src` enumeration).
 *
 * Validation is a suffix/allowlist check, NOT an equality check against the baked
 * `VITE_API_SERVER_URL`: in a dedicated tenant the resolved `apiBaseUrl` is
 * intentionally a different host (e.g. `api-lockton-uat.coronisops.com`).
 */

// Trusted base host for all hosted Coronis environments — shared platform and
// every dedicated tenant resolve to a host under this domain.
const DEFAULT_ALLOWED_BASE = "coronisops.com";

// Host bases trusted only in local/dev builds (Vite dev server, sslip.io certs).
const DEV_ALLOWED_BASES = ["localhost", "127.0.0.1", "sslip.io"];

/** Strip a leading dot and lowercase, so `.coronisops.com` and `coronisops.com` are equivalent. */
const normalizeBase = (base: string): string => base.trim().replace(/^\./, "").toLowerCase();

/** Extract the hostname (no port) from a URL string, or null if it does not parse. */
const hostnameOf = (url: string | undefined): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
};

/**
 * Returns the set of allowed API origin host bases. Sources, in order:
 *  1. The baked `VITE_API_SERVER_URL` host (always trusted — it is compiled in).
 *  2. `coronisops.com` (covers the shared platform and every dedicated tenant).
 *  3. Optional `VITE_API_ORIGIN_ALLOWLIST` (comma-separated hosts/suffixes) for
 *     white-label custom domains.
 *  4. In DEV only: localhost, 127.0.0.1, sslip.io.
 *
 * A host `h` is allowed when `h === base` or `h` ends with `"." + base` for some
 * base in this list (dot-boundary match — see {@link isAllowedApiOrigin}).
 */
export const getAllowedApiHostBases = (): string[] => {
  const bases = new Set<string>([DEFAULT_ALLOWED_BASE]);

  const bakedHost = hostnameOf(import.meta.env.VITE_API_SERVER_URL);
  if (bakedHost) bases.add(bakedHost);

  const configured = import.meta.env.VITE_API_ORIGIN_ALLOWLIST as string | undefined;
  if (configured) {
    for (const entry of configured.split(",")) {
      let base = normalizeBase(entry);
      if (!base) continue;
      // Matching is done against `URL.hostname`, which never carries a port, so a
      // port-bearing entry (e.g. `localhost:9999`) could never match. Strip the
      // port and warn rather than silently failing to allow the intended host.
      const portIdx = base.indexOf(":");
      if (portIdx !== -1) {
        console.warn(
          `[apiOrigin] VITE_API_ORIGIN_ALLOWLIST entry "${entry.trim()}" includes a port; ports are ignored (host-only match).`
        );
        base = base.slice(0, portIdx);
      }
      if (base) bases.add(base);
    }
  }

  if (import.meta.env.DEV) {
    for (const base of DEV_ALLOWED_BASES) bases.add(base);
  }

  return [...bases];
};

/**
 * True if `candidateUrl` parses to an origin whose host matches an allowed base on
 * a dot boundary. Requires `https:`; `http:` is permitted only in DEV builds.
 *
 * Dot-boundary matching (`host === base || host.endsWith("." + base)`) is what
 * makes this safe: `api-lockton-uat.coronisops.com` passes against
 * `coronisops.com`, while `evil-coronisops.com` does not.
 */
export const isAllowedApiOrigin = (candidateUrl: string): boolean => {
  let url: URL;
  try {
    url = new URL(candidateUrl);
  } catch {
    return false;
  }

  if (url.protocol !== "https:" && !(import.meta.env.DEV && url.protocol === "http:")) {
    return false;
  }

  const host = url.hostname.toLowerCase();
  return getAllowedApiHostBases().some((base) => host === base || host.endsWith(`.${base}`));
};
